import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAdminSession, AdminLoginForm, LogoutButton, useStripPasswordFromUrl } from "../lib/adminAuth";
import SEOHead from "../components/SEOHead";

export default function AdminBounced() {
  useStripPasswordFromUrl();
  const { password, login, logout, isAuthed } = useAdminSession();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loginErr, setLoginErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const fetchData = async (pwd) => {
    setBusy(true);
    setErr(null);
    try {
      const d = await api.adminBounced(pwd);
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (isAuthed && password && !data) fetchData(password);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  const onLogin = async (pwd) => {
    setBusy(true);
    setLoginErr(null);
    try {
      await login(pwd, api.adminBounced);
      const d = await api.adminBounced(pwd);
      setData(d);
    } catch (e) {
      setLoginErr(e?.response?.data?.detail || "Invalid password");
    } finally {
      setBusy(false);
    }
  };

  const onLogout = () => {
    logout();
    setData(null);
    setErr(null);
  };

  const copy = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1600);
    } catch (err) {
      console.warn("[AdminBounced] clipboard copy failed:", err);
    }
  };

  return (
    <div className="relative z-10 mx-auto min-h-[100dvh] w-full max-w-4xl px-6 py-14" data-testid="admin-bounced-screen">
      <SEOHead noindex path="/admin/bounced" title="Admin — Bounced" />
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-medium uppercase" style={{ color: "var(--muted)", letterSpacing: "0.32em" }}>
            Internal · Bounced report emails
          </p>
          <h1 className="mt-3 font-serif text-[36px]" style={{ color: "var(--gold)" }}>
            Manual follow-up
          </h1>
          <p className="mt-2 text-[13px]" style={{ color: "var(--muted)" }}>
            Buyers who paid but whose report-link email failed to reach them. Copy the private link and re-send manually.
          </p>
        </div>
        {isAuthed && <LogoutButton onLogout={onLogout} testid="admin-bounced-logout" />}
      </div>

      {!isAuthed && <AdminLoginForm onSubmit={onLogin} busy={busy} error={loginErr} testidPrefix="admin-bounced" />}

      {err && <p className="mt-6 text-[13px]" style={{ color: "#e07070" }} data-testid="admin-bounced-error">{err}</p>}

      {isAuthed && data && (
        <div className="mt-10" data-testid="admin-bounced-data">
          <p className="text-[13px]" style={{ color: "var(--muted)" }}>
            {data.count} {data.count === 1 ? "row" : "rows"}
          </p>
          {data.count === 0 ? (
            <p className="mt-8 font-serif italic" style={{ color: "var(--gold-light)" }}>
              Nothing to follow up on. All report emails delivered.
            </p>
          ) : (
            <div className="mt-6 flex flex-col gap-4">
              {data.rows.map((row, i) => (
                <div
                  key={(row.result_id || "row") + i}
                  className="panel"
                  data-testid={`bounced-row-${i}`}
                  style={{ borderColor: "rgba(224,112,112,0.35)" }}
                >
                  <div className="flex flex-wrap justify-between gap-3 text-[13px]">
                    <span style={{ color: "#e07070", letterSpacing: "0.14em", textTransform: "uppercase" }}>
                      {row.status}{row.bounce_type ? ` · ${row.bounce_type}` : ""}
                    </span>
                    <span style={{ color: "var(--muted)" }}>{row.sent_at}</span>
                  </div>

                  <dl className="mt-4 grid grid-cols-1 gap-3 text-[13px] sm:grid-cols-2">
                    <Field label="Result ID" value={row.result_id} testid={`bounced-result-${i}`} />
                    <Field label="Buyer email" value={row.email} testid={`bounced-email-${i}`} />
                    <Field label="Philosopher" value={`${row.philosopher || "?"} (${row.match_pct || "?"}%)`} />
                    <Field label="Access token" value={row.access_token} mono testid={`bounced-token-${i}`} />
                  </dl>

                  {row.report_link && (
                    <div className="mt-4">
                      <p className="text-[10px] font-medium uppercase" style={{ color: "var(--muted)", letterSpacing: "0.28em" }}>
                        Report link
                      </p>
                      <div className="mt-2 flex items-center gap-2">
                        <p
                          className="flex-1 truncate rounded border p-2 font-mono text-[12px]"
                          style={{ borderColor: "rgba(201,167,85,0.25)", background: "rgba(22,24,33,0.5)", color: "var(--text)" }}
                          data-testid={`bounced-link-${i}`}
                        >
                          {row.report_link}
                        </p>
                        <button
                          className="btn-ghost"
                          style={{ width: "auto", padding: "8px 14px" }}
                          onClick={() => copy(row.report_link, i)}
                          data-testid={`bounced-copy-${i}`}
                        >
                          {copiedIdx === i ? "Copied ✓" : "Copy"}
                        </button>
                      </div>
                    </div>
                  )}

                  {row.error && (
                    <p className="mt-3 text-[12px]" style={{ color: "#e07070" }}>
                      Error: {row.error}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono, testid }) {
  return (
    <div>
      <p className="text-[10px] font-medium uppercase" style={{ color: "var(--muted)", letterSpacing: "0.24em" }}>
        {label}
      </p>
      <p
        className={"mt-1 " + (mono ? "font-mono text-[12px]" : "font-serif text-[14px]")}
        style={{ color: "var(--text)", wordBreak: "break-all" }}
        data-testid={testid}
      >
        {value || "—"}
      </p>
    </div>
  );
}
